"use client"

// Add these imports at the top of the file
import { loadStripe } from "@stripe/stripe-js"
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { useState } from "react"
import { useAuth } from "./auth/auth-context"
import { Button } from "./ui/button"
import { Loader2, CheckCircle } from "lucide-react"
import { motion } from "framer-motion"
require('dotenv').config({ path: '.env.local' });

// Add this right after the imports
// Initialize Stripe
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || "")

// Add this component inside the PaymentScreen component
function PaymentForm({
  billingPlan,
  onSuccess,
  onError,
}: {
  billingPlan: string
  onSuccess: () => void
  onError: (message: string) => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const { user, updateUser } = useAuth()
  const [cardName, setCardName] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)

    try {
      // Create payment method
      const cardElement = elements.getElement(CardElement)

      if (!cardElement) {
        throw new Error("Card element not found")
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: user?.name || cardName,
          email: user?.email,
        },
      })

      if (error) {
        throw new Error(error.message)
      }

      // Create subscription
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Not authenticated")
      }

      const response = await fetch("/api/payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "create-subscription",
          plan: billingPlan,
          paymentMethodId: paymentMethod.id,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || "Payment failed")
      }

      const subscription = await response.json()

      // Confirm payment if needed
      if (subscription.status === "incomplete" && subscription.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(subscription.clientSecret)

        if (confirmError) {
          throw new Error(confirmError.message)
        }
      }

      // Success - update user to premium
      updateUser({ plan: "premium" })

      // Call success callback
      onSuccess()
    } catch (error: any) {
      console.error("Payment error:", error)
      onError(error.message)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="glass-card rounded-xl p-4 mb-4">
        <label className="text-sm font-light mb-2 block">Card Details</label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#ffffff",
                "::placeholder": {
                  color: "#aab7c4",
                },
              },
              invalid: {
                color: "#ef4444",
              },
            },
          }}
          className="p-3 glass rounded-xl"
        />
      </div>

      <Button
        type="submit"
        disabled={isProcessing || !stripe}
        className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:opacity-90 rounded-xl h-12 font-light"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Processing...
          </>
        ) : (
          `Pay ${billingPlan === "monthly" ? "$6.99/month" : "$67.10/year"}`
        )}
      </Button>
    </form>
  )
}

// Replace the form in the PaymentScreen component with this:
// Find the motion.form element and replace it with:

// PaymentScreen.tsx
import type React from "react"

interface PaymentScreenProps {
  billingPlan: "monthly" | "yearly"
  setActiveScreen: (screen: string) => void
}

const PaymentScreen: React.FC<PaymentScreenProps> = ({ billingPlan = "yearly", setActiveScreen }) => {
  const [isSuccess, setIsSuccess] = useState(false)

  return (
    <>
      {!isSuccess ? (
        <Elements stripe={stripePromise}>
          <PaymentForm
            billingPlan={billingPlan}
            onSuccess={() => setIsSuccess(true)}
            onError={(message) => alert(message)}
          />
        </Elements>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center text-center py-12"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-400" />
          </div>
          <h2 className="text-xl font-medium mb-2">Payment Successful!</h2>
          <p className="text-zinc-400 mb-6">You've been upgraded to Premium</p>
          <Button
            onClick={() => setActiveScreen("analyze")}
            className="gradient-bg text-white hover:opacity-90 rounded-xl px-6"
          >
            Continue to App
          </Button>
        </motion.div>
      )}
    </>
  )
}

export default PaymentScreen

